import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import EmergencyToggle from "@/components/EmergencyToggle";
import FormLogo from "@/components/FormLogo";
import BillsTable from "@/components/BillsTable";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, FileField, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection, makeUploader,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection
} from "@/components/FormShell";

function newBill(i) { return { sr_no: String(i + 1), bill_type: "", attachment: null }; }

export default function NewReimbursement() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  const [bills, setBills] = useState([newBill(0), newBill(1), newBill(2)]);
  const [form, setForm] = useState({
    staffName: user?.name || "", staffEmail: user?.email || "",
    staffContact: "",
    mainDept: "", subDept: "", hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",
    employeeName: "", empCode: "", travelApprovedBy: "",
    purposeOfTravel: "",
    // Local travel table + D.A summary
    daFromDate: "", daToDate: "", daNoOfDays: "", daAmountCalculated: "",
    is_emergency: false, emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  // Phase 25a (UX fix) — reactive validity. `missingRows` and
  // `allValid` are recomputed on EVERY form state change, so the
  // Submit button is disabled from the very first render and the
  // inline error block is visible without the user having to click
  // Submit once to discover the problem.
  const missingRows = useMemo(() => {
    return bills
      .map((b, i) => ({ b, i }))
      .filter(({ b }) => (b.sr_no || "").trim() && (b.bill_type || "").trim())
      .filter(({ b }) => !b.attachment)
      .map(({ i }) => i);
  }, [bills]);

  const filledBillCount = useMemo(() => bills.filter(
    (b) => (b.sr_no || "").trim() && (b.bill_type || "").trim(),
  ).length, [bills]);

  const headerFieldsValid = useMemo(() => {
    const hodOk = form.is_hod_self === true
                   || form.is_admin1_self === true
                   || String(form.hodApproval || "").trim().length > 0;
    const required = ["staffName", "staffEmail", "mainDept", "subDept",
                       "employeeName", "empCode", "purposeOfTravel"];
    const fieldsOk = required.every((k) => String(form[k] || "").trim());
    const emergencyOk = !form.is_emergency
                         || (form.emergency_reason || "").trim().length >= 10;
    return hodOk && fieldsOk && emergencyOk;
  }, [form]);

  const allValid = headerFieldsValid
                    && filledBillCount >= 1
                    && missingRows.length === 0;

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onUpload = makeUploader(api, setAttachments, setUploading, toast, formatApiError);

  const validate = () => {
    // Phase 25a (UX fix) — all validation toasts share one id so
    // Sonner replaces the previous toast instead of stacking.
    const toastId = "reimbursement-validation";
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?",
                   { id: toastId });
      return false;
    }
    for (const k of ["staffName", "staffEmail", "mainDept", "subDept",
                      "employeeName", "empCode", "purposeOfTravel"]) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill: ${k}`, { id: toastId });
        return false;
      }
    }
    if (filledBillCount === 0) {
      toast.error("Add at least 1 bill row (S.No + Bill Type).", { id: toastId });
      return false;
    }
    // Attachment-row errors are already shown inline + beside each
    // row. Do NOT fire another toast — the user can already see which
    // rows are offending.
    if (missingRows.length > 0) return false;
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).", { id: toastId });
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const filled = bills.filter((b) => (b.sr_no || "").trim() && (b.bill_type || "").trim());
      const { data } = await api.post("/requisitions/reimbursement", {
        form_type: "reimbursement", parent_ref_code: parentRefCode || undefined,
        form_data: { ...stripSelfSkipTransients(form), bills: filled, attachments },
      });
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#8a3f19] font-semibold">
          VGWS-RMB-2026 · Reimbursement
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Reimbursement Form
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">All disbursement is as per school policy and entitlement.</p>

        <TopNotice>
          <div className="font-serif font-semibold mb-1 flex items-center gap-2">
            <AlertCircle size={16} /> Important
          </div>
          <p>Attach every bill separately per row for cleaner reconciliation.</p>
        </TopNotice>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="reimbursement-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({ ...f, ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
              }));
              setParentRefCode(ref);
            }}
          />

          <ApplicantSpocSection form={form} setField={setField} />
          <SelfSkipSection form={form} setField={setField} userRole={user?.role} />
          <RequestedBySection form={form} setField={setField} depts={depts} hods={hods} subDepts={subDepts} hodOptional={isHodDropdownOptional(form)} />

          <Section numeral="III" title="Employee details">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Name of the Employee" required>
                <input className="input-vgws" required value={form.employeeName}
                       onChange={(e) => setField("employeeName", e.target.value)}
                       data-testid="field-employeeName" />
              </Field>
              <Field label="Emp. Code" required>
                <input className="input-vgws" required value={form.empCode}
                       onChange={(e) => setField("empCode", e.target.value)}
                       data-testid="field-empCode" />
              </Field>
              <Field label="Travel approved by">
                <input className="input-vgws" value={form.travelApprovedBy}
                       onChange={(e) => setField("travelApprovedBy", e.target.value)}
                       data-testid="field-travelApprovedBy" />
              </Field>
            </div>
          </Section>

          <Section numeral="IV" title="Purpose of Travel">
            <Field label="Purpose of Travel" required>
              <textarea className="input-vgws min-h-[80px]" required value={form.purposeOfTravel}
                        onChange={(e) => setField("purposeOfTravel", e.target.value)}
                        data-testid="field-purposeOfTravel" />
            </Field>
          </Section>

          <Section numeral="V" title="D.A / Food / Stay">
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="From Date">
                <input type="date" className="input-vgws" value={form.daFromDate}
                       onChange={(e) => setField("daFromDate", e.target.value)}
                       data-testid="field-daFromDate" />
              </Field>
              <Field label="To Date">
                <input type="date" className="input-vgws" value={form.daToDate}
                       onChange={(e) => setField("daToDate", e.target.value)}
                       data-testid="field-daToDate" />
              </Field>
              <Field label="No. of Days">
                <input type="number" min="0" className="input-vgws" value={form.daNoOfDays}
                       onChange={(e) => setField("daNoOfDays", e.target.value)}
                       data-testid="field-daNoOfDays" />
              </Field>
              <Field label="Amount Calculated">
                <input type="number" min="0" className="input-vgws" value={form.daAmountCalculated}
                       onChange={(e) => setField("daAmountCalculated", e.target.value)}
                       data-testid="field-daAmountCalculated" />
              </Field>
            </div>
          </Section>

          <Section numeral="VI" title="Local travel bills">
            <BillsTable bills={bills} setBills={setBills}
                        api={api} toast={toast} formatApiError={formatApiError}
                        missingRows={missingRows} />
          </Section>

          <Section numeral="VII" title="Additional enclosures">
            <FileField label="Supporting documents" fieldName="supporting"
                       accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                       attachments={attachments} onChange={(e) => onUpload("supporting", e)}
                       disabled={uploading} testid="attach-supporting" optional />
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required.
            </p>
            <button type="submit" disabled={submitting || uploading || !allValid} className="btn-vgws"
                    data-testid="submit-reimbursement-button">
              <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
